"use client";

import React from "react";
import { ArrowUpRight, Mail, MapPin, Phone, Loader, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const contactInfo = [{
  label: "HEADQUARTERS",
  value: "Arbor Pro, 1200 Texan Trail, Fort Worth, TX",
  icon: MapPin
}, {
  label: "EMAIL",
  value: "arborprotexas@gmail.com",
  icon: Mail
}, {
  label: "DISPATCH",
  value: "+1 817-503-3131",
  icon: Phone
}];
const socialLinks = [{
  label: "INSTAGRAM",
  href: "#"
}, {
  label: "FACEBOOK",
  href: "#"
}, {
  label: "YELP",
  href: "#"
}, {
  label: "NEXTDOOR",
  href: "#"
}];
function CornerTicks() {
  return <>
      <span className="absolute top-0 left-0 w-4 h-px bg-accent" aria-hidden="true" />
      <span className="absolute top-0 left-0 h-4 w-px bg-accent" aria-hidden="true" />
      <span className="absolute top-0 right-0 w-4 h-px bg-accent" aria-hidden="true" />
      <span className="absolute top-0 right-0 h-4 w-px bg-accent" aria-hidden="true" />
      <span className="absolute bottom-0 left-0 w-4 h-px bg-accent" aria-hidden="true" />
      <span className="absolute bottom-0 left-0 h-4 w-px bg-accent" aria-hidden="true" />
      <span className="absolute bottom-0 right-0 w-4 h-px bg-accent" aria-hidden="true" />
      <span className="absolute bottom-0 right-0 h-4 w-px bg-accent" aria-hidden="true" />
    </>;
}
function AssessmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border rounded-lg text-center shadow-md">
        <div className="inline-flex items-center justify-center size-14 rounded-lg bg-accent/20 text-accent mb-4">
          <CheckCircle2 className="size-7" />
        </div>
        <h3 className="font-default text-xl font-bold mb-2">Estimate Requested</h3>
        <p className="text-muted-foreground text-sm font-default mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background border-border text-foreground h-11 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" rules={{
        required: "Phone is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(817) 000-0000" className="bg-background border-border text-foreground h-11 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="bg-background border-border text-foreground h-11 rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Service Interest</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full bg-background border-border text-foreground h-11 rounded-lg">
                  <SelectValue placeholder="Select tree service type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-card border-border">
                <SelectItem value="pruning">Tree Pruning & Maintenance</SelectItem>
                <SelectItem value="removal">Safe Tree Removal</SelectItem>
                <SelectItem value="health">Arborist Health Evaluation</SelectItem>
                <SelectItem value="other">Other Tree Services</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Project Details / Location</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your trees, property city, and any specific concerns..." className="bg-background border-border text-foreground min-h-[120px] rounded-lg p-3 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/95 transition-all shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            TRANSMITTING REQUEST...
          </> : "GET YOUR FREE ESTIMATE"}
      </Button>
    </Form>;
}
export default function RequestYourEstimate() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3767" id="request-your-estimate" className="bg-background text-foreground py-24 lg:py-32 px-4 sm:px-6 lg:px-12 relative overflow-hidden">
      <div className="absolute top-8 left-8 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/40 hidden lg:block">
        §06 / FREE ESTIMATE REQUEST
      </div>
      <div className="absolute top-8 right-8 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/40 hidden lg:block">
        LOC: DFW / ZONE: 8A
      </div>

      <div ref={ref} className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="lg:col-span-5 flex flex-col">
          <div className="inline-flex items-center gap-2 mb-6">
            <ShieldCheck className="size-4 text-accent" />
            <p className="font-mono text-xs uppercase tracking-widest text-accent">
              SECURE CONSULTATION REQUEST
            </p>
          </div>

          <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6">
            <span className="block text-foreground">GET YOUR</span>
            <span className="block text-accent">ESTIMATE</span>
          </h2>

          <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["LocalBusiness", "ProfessionalService"],
            "name": "Arbor Pro",
            "description": "Professional arborist consultation and tree care services in the DFW area. Get a free arborist estimate for pruning, removals, and health evaluations.",
            "telephone": "+1 817-503-3131",
            "areaServed": ["Fort Worth", "DFW", "Tarrant County"],
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1200 Texan Trail",
              "addressLocality": "Fort Worth",
              "addressRegion": "TX",
              "addressCountry": "US"
            }
          })
        }} />

          <p className="text-muted-foreground text-base leading-relaxed max-w-[50ch] mb-10 font-default">
            <span className="font-bold text-accent">Free ISA certified arborist consultation.</span> Secure a professional arborist estimate for your residential canopy. Our team provides expert arborist consultation and surgical precision for pruning, removals, and health evaluations across the Fort Worth metroplex. Request your arborist estimate today to ensure the longevity of your landscape.
          </p>

          <div className="border-t border-border mb-10">
            {contactInfo.map((info, i) => {
            const Icon = info.icon;
            return <div key={i} data-index={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-4 border-b border-border">
                  <div className="flex items-center gap-2 sm:w-40">
                    <ArrowUpRight className="size-3 text-accent shrink-0" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {info.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <Icon className="size-4 text-muted-foreground shrink-0 hidden sm:block" />
                    <span className="text-foreground text-sm font-default">{info.value}</span>
                  </div>
                </div>;
          })}
          </div>

          <div className="flex flex-wrap gap-2">
            {socialLinks.map((social, i) => <Link key={i} data-index={i} to={social.href} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-accent px-3 py-2 border border-border hover:border-accent transition-colors rounded-lg bg-card">
                {social.label}
              </Link>)}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative bg-card p-6 sm:p-10 lg:p-12 border border-border rounded-lg shadow-lg">
            <CornerTicks />

            <div className="flex items-center justify-between mb-8 pb-4 border-b border-border">
              <p className="font-mono text-xs uppercase tracking-widest text-accent font-semibold">
                CONSULTATION PORTAL
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                SECURE FORM 01
              </p>
            </div>

            <div className="flex flex-col gap-6">
              <FormProvider formId="estimate_form_get_quote" sectionName="request-your-estimate" defaultValues={{
              name: "",
              email: "",
              phone: "",
              service_interest: "",
              message: ""
            }} successMessage="Thank you! We will contact you within 24 business hours.">
                <AssessmentForm />
              </FormProvider>
              
              <div className="p-4 rounded-lg bg-background/60 border border-border/80">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/80 leading-relaxed">
                  [SAFETY PROTOCOL] All estimates are performed by ISA Certified Arborists. We respect your privacy and property. Response within 24 business hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}